import type { CSSProperties } from "react";
import styled, { css } from "styled-components";
import { colors } from "../styles/variables";

type ButtonSize = "default" | "medium" | "small";

type ButtonProps = {
  disabled?: boolean;
  onClick?: () => void;
  size?: ButtonSize;
  style?: CSSProperties;
  type?: "button" | "submit" | "reset";
  value: string;
};

const Button = ({
  disabled,
  onClick,
  size = "default",
  style,
  type = "button",
  value,
}: ButtonProps) => {
  const handleClick = () => {
    if (disabled) return;

    onClick?.();
  };

  return (
    <StyledButton
      $disabled={disabled}
      $size={size}
      disabled={disabled}
      onClick={handleClick}
      style={style}
      type={type}
    >
      {value}
    </StyledButton>
  );
};

const StyledButton = styled.button<{ $disabled?: boolean; $size: ButtonSize }>`
  display: inline-block;
  font-family: inherit;
  font-weight: 400;
  text-transform: uppercase;
  text-align: center;
  background: ${colors.primary};
  color: ${colors.white};
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid ${colors.primary};
  border-radius: 0.6rem;

  &:hover {
    background: transparent;
    border-color: ${colors.black};
    color: ${colors.black};
  }

  ${({ $size }) =>
    $size === "medium"
      ? css`
          padding: 0.9rem 2.5rem;
          font-size: 1rem;
        `
      : $size === "small"
        ? css`
            font-size: 0.9rem;
            letter-spacing: 0.12em;
            padding: 0.5rem 1.5rem;

            @media (max-width: 768px) {
              font-size: 0.8rem;
              padding: 0.6rem 1.35rem;
              letter-spacing: 0.1em;
            }
          `
        : css`
            font-size: 1.2rem;
            letter-spacing: 0.2em;
            padding: 1rem 3rem;
            border: 3px solid ${colors.primary};
            border-radius: 1rem;
          `};

  /* after the size block so it overrides the size-specific border color */
  ${({ $disabled }) =>
    $disabled &&
    css`
      background: ${colors.gray};
      border-color: ${colors.gray};
      color: ${colors.white};
      cursor: not-allowed;
      opacity: 0.7;

      &:hover {
        background: ${colors.gray};
        border-color: ${colors.gray};
        color: ${colors.white};
      }
    `};
`;

export default Button;
